import { useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ChevronDown, LogIn, UserPlus, Sparkles, LayoutGrid, BadgePercent, CircleHelp, Home, Wallet, BookOpen, Package, ChevronRight } from "lucide-react";
import { useLang } from "../i18n";
import { toEnPath, toTrPath } from "../langRouting";
import { useSiteSettings, goTo, pick } from "../settings";
import { ModulesMegaMenu, CORE_ICONS } from "./ModulesMegaMenu";
import { CORE_MODULES } from "../moduleData";
import { NAV_PACKAGES as PACKAGES, PACKAGE_ORDER } from "../packageNav";

const APP_URL = "https://giris.parasyo.com";

const DEFAULT_LINKS = [
  { label_tr: "Modüller", label_en: "Modules", href: "#moduller" },
  { label_tr: "Planlar", label_en: "Plans", href: "#planlar" },
  { label_tr: "Kitapçık", label_en: "Booklet", href: "/kitapcik" },
  { label_tr: "Destek", label_en: "Support", href: "/destek" },
];

const NAV_ICONS = {
  "#moduller": LayoutGrid,
  "#planlar": Package,
  "/kitapcik": BookOpen,
  "/destek": CircleHelp,
};

const localText = (value, lang) => value?.[lang] || value?.tr || "";

export const Header = () => {
  const { lang, setLang, t } = useLang();
  // /en ayna ağacı: dil değişimi URL üzerinden yapılır (LangSync dili URL'ye sabitler)
  const location = useLocation();
  const switchLang = (l) => {
    if (l === lang) return;
    setLang(l);
    navigate(l === "en" ? toEnPath(location.pathname) : toTrPath(location.pathname));
  };
  const settings = useSiteSettings();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [modulesOpen, setModulesOpen] = useState(false);
  const [plansOpen, setPlansOpen] = useState(false);
  const [announcementDismissed, setAnnouncementDismissed] = useState(false);

  const header = settings.header || {};
  const loginUrl = header.login_url || APP_URL;
  const registerUrl = header.register_url || `${APP_URL}/kayit`;
  // Para birimi sayfa diline bağlı (kullanıcı kuralı): EN → $ (duyuru metni admin'den düzenlenebilir)
  const announcementText = pick(header, "announcement_text", lang, "");
  const announcementLinkLabel = pick(header, "announcement_link_label", lang, "");
  const announcementHref = header.announcement_link_href || "";
  const announcementDismissible = header.announcement_dismissible !== false;
  const announcementSignature = [
    header.announcement_text_tr || "",
    header.announcement_text_en || "",
    announcementHref,
  ].join("|");
  const announcementStorageKey = "parasyo-announcement-dismissed";

  useEffect(() => {
    setAnnouncementDismissed(localStorage.getItem(announcementStorageKey) === announcementSignature);
  }, [announcementSignature]);

  const announcementVisible = header.announcement_enabled === true && !!announcementText && (!announcementDismissible || !announcementDismissed);

  // Bant yüksekliğini gerçek boyutundan ölç (mobilde metin iki satıra sarabilir)
  const bannerRef = useRef(null);
  useEffect(() => {
    const setHeight = () =>
      document.documentElement.style.setProperty(
        "--announcement-height",
        announcementVisible && bannerRef.current ? `${bannerRef.current.offsetHeight}px` : "0px"
      );
    setHeight();
    const el = bannerRef.current;
    if (!announcementVisible || !el) return;
    const ro = new ResizeObserver(() => requestAnimationFrame(setHeight));
    ro.observe(el);
    return () => ro.disconnect();
  }, [announcementVisible]);

  const dismissAnnouncement = () => {
    localStorage.setItem(announcementStorageKey, announcementSignature);
    setAnnouncementDismissed(true);
  };

  // Metindeki ilk fiyat üstü çizili (eski), sonraki fiyat vurgulu (yeni) gösterilir.
  // Tek fiyat varsa (yurtdışı $ varyantı) üstü çizilmez, vurgulanır.
  const renderAnnouncementText = (text) => {
    const priceRe = /(\$\d+(?:[.,]\d+)?(?:\/(?:ay|mo))?|\d+(?:[.,]\d+)?\s*TL)/g;
    const total = (text.match(priceRe) || []).length;
    let seen = 0;
    return text.split(priceRe).map((part, i) => {
      if (!/^(\$\d+(?:[.,]\d+)?(?:\/(?:ay|mo))?|\d+(?:[.,]\d+)?\s*TL)$/.test(part)) return part;
      seen += 1;
      return total > 1 && seen === 1 ? (
        <span key={i} className="line-through opacity-70">{part}</span>
      ) : (
        <span key={i} className="mx-0.5 inline-flex items-center rounded-full bg-white px-2 py-0.5 font-extrabold text-operofy-blue">{part}</span>
      );
    });
  };

  const links = (header.nav_links?.length ? header.nav_links : DEFAULT_LINKS)
    // Boş/hatalı satırlar (panelde boş eklenip kaydedilenler) menüyü bozmasın
    .filter((l) => l && l.href && (l.label_tr || l.label_en))
    .map((l) => ({
      id: l.href.replace(/^[#/]+/, "").replace(/\//g, "-") || "link",
      // Aktif dilde etiket boşsa diğer dile düş (boş buton görünmesin)
      label: pick(l, "label", lang, "") || l.label_tr || l.label_en || "",
      href: l.href,
    }));

  return (
    <header className="fixed top-0 left-0 right-0 z-50 border-b border-white/10 bg-black/60 backdrop-blur-xl">
      <div className="h-[3px] w-full bg-gradient-to-r from-[#0a6b32] via-[#17a851] to-[#0a6b32]" data-testid="header-accent" />
      {announcementVisible && (
        <div
          ref={bannerRef}
          className="banner-shine force-dark relative overflow-hidden border-b border-[#17a851]/40 bg-gradient-to-r from-[#0a6b32] via-[#12a04b] to-[#0a6b32] text-white"
          data-testid="announcement-bar"
        >
          <div className="relative z-10 mx-auto flex min-h-10 max-w-7xl items-center justify-center px-10 py-2 lg:px-12">
            <div className="flex min-w-0 flex-wrap items-center justify-center gap-x-2.5 gap-y-1 text-center">
              <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-[#F2B25C] px-2.5 py-0.5 text-[10px] font-extrabold tracking-wider text-[#0B3520]" data-testid="announcement-badge">
                <Sparkles size={11} strokeWidth={2.5} />
                {lang === "en" ? "NEW" : "YENİ"}
              </span>
              <p className="text-[11px] font-semibold leading-snug tracking-wide sm:text-xs" data-testid="announcement-text">
                {renderAnnouncementText(announcementText)}
              </p>
              {announcementHref && announcementLinkLabel && (
                <button
                  data-testid="announcement-link"
                  onClick={() => goTo(announcementHref, navigate)}
                  className="inline-flex shrink-0 items-center gap-0.5 rounded-full bg-white px-3.5 py-1 text-[11px] font-extrabold text-operofy-blue shadow-sm transition-transform duration-200 hover:scale-105 active:scale-95"
                >
                  {announcementLinkLabel}
                  <ChevronRight size={12} strokeWidth={2.5} />
                </button>
              )}
            </div>
            {announcementDismissible && (
              <button
                data-testid="announcement-close"
                onClick={dismissAnnouncement}
                aria-label={lang === "en" ? "Close announcement" : "Duyuruyu kapat"}
                className="absolute right-3 flex h-7 w-7 items-center justify-center rounded-full text-white/80 transition-colors duration-200 hover:bg-white/10 hover:text-white"
              >
                <X size={14} strokeWidth={2} />
              </button>
            )}
          </div>
        </div>
      )}
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-6 lg:px-8">
        <button
          data-testid="logo-home-btn"
          onClick={() => {
            if (window.location.pathname !== "/") navigate("/");
            setTimeout(() => window.scrollTo({ top: 0, behavior: "smooth" }), 60);
          }}
          className="transition-opacity duration-200 hover:opacity-70"
        >
          <img src="/assets/brand/parasyo-white.png" alt="Parasyo" width="1443" height="288" className="brand-dark h-6 w-auto" data-testid="logo-img" />
          <img src="/assets/brand/parasyo-light-bg.png" alt="Parasyo" width="1443" height="288" className="brand-light h-6 w-auto" />
        </button>

        <nav className="hidden items-center lg:flex" data-testid="main-nav">
          {links.map((l, i) => (
            <div key={`${l.id}-${i}`} className="flex items-center">
              {i > 0 && <span className="mx-2 h-4 w-px bg-white/15" />}
              {l.href === "#moduller" ? (
                <ModulesMegaMenu label={l.label} lang={lang} />
              ) : l.href === "#planlar" ? (
                <div className="group relative" data-testid="plans-menu">
                  <button
                    data-testid="nav-planlar"
                    className="flex items-center gap-1.5 px-2 py-2 text-sm font-semibold text-white/70 transition-colors duration-200 hover:text-operofy-blue"
                  >
                    {l.label}
                    <ChevronDown size={14} strokeWidth={2} className="transition-transform duration-200 group-hover:rotate-180" />
                  </button>
                <div className="invisible absolute left-1/2 top-full -translate-x-1/2 pt-2 opacity-0 transition-all duration-200 group-hover:visible group-hover:opacity-100">
                  <div className="w-80 overflow-hidden rounded-2xl border border-white/10 bg-operofy-darksurface p-2 shadow-2xl" data-testid="plans-dropdown">
                    {PACKAGE_ORDER.map((slug) => {
                      const p = PACKAGES[slug];
                      return (
                        <button
                          key={slug}
                          data-testid={`plans-item-${slug}`}
                          onClick={() => navigate(`/paket/${slug}`)}
                          className="flex w-full items-center gap-3 rounded-xl px-3 py-3 text-left transition-colors duration-200 hover:bg-white/5"
                        >
                          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-operofy-blue/10 text-operofy-blue">
                            <Package size={16} strokeWidth={1.8} />
                          </span>
                          <span className="min-w-0">
                            <span className="flex items-center gap-2 text-sm font-bold text-white">
                              {p.name}
                              <span className="font-mono-tech text-[10px] font-medium tracking-wide text-operofy-blue">{lang === "en" ? p.price_foreign_en || p.price_en : p.price_tr}</span>
                            </span>
                            <span className="mt-0.5 block truncate text-xs text-white/50">{p.tagline}</span>
                          </span>
                          <ChevronRight size={14} strokeWidth={2} className="ml-auto shrink-0 text-white/30" />
                        </button>
                      );
                    })}
                    <button
                      data-testid="plans-compare"
                      onClick={() => goTo("/karsilastir", navigate)}
                      className="mt-1 w-full rounded-xl border-t border-white/10 px-3 py-3 text-left text-xs font-bold text-operofy-blue transition-colors duration-200 hover:bg-white/5"
                    >
                      {lang === "en" ? "Compare all plans" : "Tüm planları karşılaştır"}
                    </button>
                  </div>
                </div>
                </div>
              ) : (
                <button
                  data-testid={`nav-${l.id}`}
                  onClick={() => goTo(l.href, navigate)}
                  className="px-2 py-2 text-sm font-semibold text-white/70 transition-colors duration-200 hover:text-operofy-blue"
                >
                  {l.label}
                </button>
              )}
            </div>
          ))}
        </nav>

        <div className="hidden items-center gap-4 lg:flex">
          <div className="flex items-center gap-1 rounded-full border border-white/10 p-1" data-testid="lang-toggle">
            {["tr", "en"].map((l) => (
              <button
                key={l}
                data-testid={`lang-toggle-${l}`}
                onClick={() => switchLang(l)}
                className={`rounded-full px-3 py-1 text-xs uppercase tracking-widest transition-colors duration-200 ${
                  lang === l ? "bg-white text-black" : "text-white/60 hover:text-white"
                }`}
              >
                {l}
              </button>
            ))}
          </div>
          <div className="group relative" data-testid="online-islemler">
            <button
              data-testid="online-islemler-btn"
              className="flex items-center gap-2 whitespace-nowrap rounded-full border-2 border-operofy-blue bg-transparent px-5 py-2 text-sm font-bold text-operofy-blue transition-all duration-200 hover:bg-operofy-blue hover:text-operofy-onblue active:scale-95"
            >
              {t.nav.online}
              <ChevronDown size={14} strokeWidth={2} className="transition-transform duration-200 group-hover:rotate-180" />
            </button>
            <div className="invisible absolute right-0 top-full pt-2 opacity-0 transition-all duration-200 group-hover:visible group-hover:opacity-100">
              <div className="w-48 overflow-hidden rounded-2xl border border-white/10 bg-operofy-darksurface p-1.5 shadow-2xl" data-testid="online-islemler-menu">
                <a
                  href={loginUrl}
                  data-testid="online-login-link"
                  className="flex items-center gap-3 rounded-xl px-4 py-3 text-sm text-white/80 transition-colors hover:bg-white/5 hover:text-white"
                >
                  <LogIn size={15} strokeWidth={1.5} className="text-operofy-blue" />
                  {t.nav.login}
                </a>
                <a
                  href={registerUrl}
                  data-testid="online-register-link"
                  className="flex items-center gap-3 rounded-xl px-4 py-3 text-sm text-white/80 transition-colors hover:bg-white/5 hover:text-white"
                >
                  <UserPlus size={15} strokeWidth={1.5} className="text-operofy-blue" />
                  {t.nav.register}
                </a>
              </div>
            </div>
          </div>
        </div>

        <button
          data-testid="mobile-menu-btn"
          onClick={() => setOpen(!open)}
          className="text-white lg:hidden"
          aria-label="Menu"
        >
          {open ? <X size={22} strokeWidth={1.5} /> : <Menu size={22} strokeWidth={1.5} />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className="overflow-hidden border-t border-white/10 bg-black/90 backdrop-blur-xl lg:hidden"
            data-testid="mobile-menu"
          >
            <div className="flex flex-col gap-1 px-6 py-6">
              {links.map((l, i) => (
                l.href === "#planlar" ? (
                  <div key={`${l.id}-${i}`}>
                    <button
                      data-testid={`mobile-nav-${l.id}`}
                      onClick={() => setPlansOpen(!plansOpen)}
                      className="flex w-full items-center gap-3 rounded-2xl px-3 py-3 text-left text-lg font-semibold text-white/80 transition-colors hover:bg-white/5 hover:text-white"
                    >
                      <Package size={18} strokeWidth={1.8} className="text-operofy-blue" />
                      {l.label}
                      <ChevronDown size={16} strokeWidth={2} className={`ml-auto transition-transform duration-200 ${plansOpen ? "rotate-180" : ""}`} />
                    </button>
                    <AnimatePresence>
                      {plansOpen && (
                        <motion.div
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: "auto" }}
                          exit={{ opacity: 0, height: 0 }}
                          transition={{ duration: 0.25 }}
                          className="overflow-hidden"
                          data-testid="mobile-plans-panel"
                        >
                          <div className="mt-1 flex flex-col gap-1 rounded-2xl border border-white/10 p-2">
                            {PACKAGE_ORDER.map((slug) => {
                              const p = PACKAGES[slug];
                              return (
                                <button
                                  key={slug}
                                  data-testid={`mobile-plan-${slug}`}
                                  onClick={() => { navigate(`/paket/${slug}`); setOpen(false); }}
                                  className="flex items-center gap-3 rounded-xl px-3 py-2.5 text-left text-sm font-semibold text-white/75 transition-colors hover:bg-white/5 hover:text-white"
                                >
                                  <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-white/5 text-operofy-blue">
                                    <Package size={15} strokeWidth={1.8} />
                                  </span>
                                  <span className="flex-1">{p.name}</span>
                                  <span className="font-mono-tech text-[10px] text-operofy-blue">{lang === "en" ? p.price_foreign_en || p.price_en : p.price_tr}</span>
                                </button>
                              );
                            })}
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                ) : l.href === "#moduller" ? (
                  <div key={`${l.id}-${i}`}>
                    <button
                      data-testid={`mobile-nav-${l.id}`}
                      onClick={() => setModulesOpen(!modulesOpen)}
                      className="flex w-full items-center gap-3 rounded-2xl px-3 py-3 text-left text-lg font-semibold text-white/80 transition-colors hover:bg-white/5 hover:text-white"
                    >
                      <LayoutGrid size={18} strokeWidth={1.8} className="text-operofy-blue" />
                      {l.label}
                      <ChevronDown size={16} strokeWidth={2} className={`ml-auto transition-transform duration-200 ${modulesOpen ? "rotate-180" : ""}`} />
                    </button>
                    <AnimatePresence>
                      {modulesOpen && (
                        <motion.div
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: "auto" }}
                          exit={{ opacity: 0, height: 0 }}
                          transition={{ duration: 0.25 }}
                          className="overflow-hidden"
                          data-testid="mobile-modules-panel"
                        >
                          <div className="mt-1 flex flex-col gap-1 rounded-2xl border border-white/10 p-2">
                            {CORE_MODULES.map((item) => {
                              const Icon = CORE_ICONS[item.slug] || Wallet;
                              return (
                                <button
                                  key={item.slug}
                                  data-testid={`mobile-module-${item.slug}`}
                                  onClick={() => { navigate(item.href); setOpen(false); }}
                                  className="flex items-center gap-3 rounded-xl px-3 py-2.5 text-left text-sm font-semibold text-white/75 transition-colors hover:bg-white/5 hover:text-white"
                                >
                                  <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-white/5 text-operofy-blue">
                                    <Icon size={15} strokeWidth={1.8} />
                                  </span>
                                  {localText(item.title, lang)}
                                </button>
                              );
                            })}
                            <button
                              data-testid="mobile-module-home-plus"
                              onClick={() => { navigate("/home-plus"); setOpen(false); }}
                              className="flex items-center gap-3 rounded-xl bg-operofy-blue/10 px-3 py-2.5 text-left text-sm font-semibold text-operofy-blue transition-colors hover:bg-operofy-blue/15"
                            >
                              <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-operofy-blue text-white">
                                <Home size={15} strokeWidth={1.8} />
                              </span>
                              Home+
                            </button>
                            <button
                              data-testid="mobile-module-pos"
                              onClick={() => { navigate("/pos"); setOpen(false); }}
                              className="flex items-center gap-3 rounded-xl px-3 py-2.5 text-left text-sm font-semibold text-white/75 transition-colors hover:bg-white/5 hover:text-white"
                            >
                              <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-white/5 text-operofy-blue">
                                <Wallet size={15} strokeWidth={1.8} />
                              </span>
                              {lang === "en" ? "Fast Sale (POS)" : "Hızlı Satış (POS)"}
                            </button>
                            <button
                              data-testid="mobile-module-pro-modules"
                              onClick={() => { navigate("/paket/parasyo-pro"); setOpen(false); }}
                              className="flex items-center gap-3 rounded-xl px-3 py-2.5 text-left text-sm font-semibold text-white/75 transition-colors hover:bg-white/5 hover:text-white"
                            >
                              <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-white/5 text-operofy-blue">
                                <Package size={15} strokeWidth={1.8} />
                              </span>
                              {lang === "en" ? "CRM · DMS · HR+ · Attendance" : "CRM · DMS · İK+ · PDKS"}
                            </button>
                            <div className="mt-1 grid grid-cols-1 gap-2 border-t border-white/10 pt-2" data-testid="mobile-modules-groups">
                              <button
                                data-testid="mobile-modules-free"
                                onClick={() => { navigate("/moduller?plan=free"); setOpen(false); }}
                                className="rounded-xl bg-operofy-blue/10 px-4 py-3 text-left text-sm font-semibold text-operofy-blue"
                              >
                                {lang === "en" ? "ParasyoFree Coverage" : "ParasyoFree Kapsamı"}
                              </button>
                              <button
                                data-testid="mobile-modules-addons"
                                onClick={() => { navigate("/moduller"); setOpen(false); }}
                                className="rounded-xl bg-white/5 px-4 py-3 text-left text-sm font-semibold text-white/75"
                              >
                                {lang === "en" ? "All Modules" : "Tüm Modüller"}
                              </button>
                            </div>
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                ) : (
                  <button
                    key={`${l.id}-${i}`}
                    data-testid={`mobile-nav-${l.id}`}
                    onClick={() => {
                      goTo(l.href, navigate);
                      setOpen(false);
                    }}
                    className="flex items-center gap-3 rounded-2xl px-3 py-3 text-left text-lg font-semibold text-white/80 transition-colors hover:bg-white/5 hover:text-white"
                  >
                    {(() => {
                      const Icon = NAV_ICONS[l.href] || Sparkles;
                      return <Icon size={18} strokeWidth={1.8} className="text-operofy-blue" />;
                    })()}
                    {l.label}
                  </button>
                )
              ))}
              <div className="mt-4 flex items-center gap-3">
                {["tr", "en"].map((l) => (
                  <button
                    key={l}
                    data-testid={`mobile-lang-toggle-${l}`}
                    onClick={() => switchLang(l)}
                    className={`rounded-full border border-white/10 px-4 py-2 text-xs uppercase tracking-widest ${
                      lang === l ? "bg-white text-black" : "text-white/60"
                    }`}
                  >
                    {l}
                  </button>
                ))}
                <a
                  data-testid="mobile-online-login"
                  href={loginUrl}
                  className="rounded-full bg-white px-5 py-2 text-sm font-semibold text-black active:scale-95"
                >
                  {t.nav.login}
                </a>
                <a
                  data-testid="mobile-online-register"
                  href={registerUrl}
                  className="rounded-full border border-white/20 px-5 py-2 text-sm font-semibold text-white active:scale-95"
                >
                  {t.nav.register}
                </a>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};
